/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `VideoThumbnail` for the Caintra Connect frontend.
 * @module components/VideoTutorialsSection/VideoThumbnail
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styles from "./VideoLibrary.module.css";
import { generateAssetUrl } from "../../utils/common.js";

const VideoThumbnail = ({ videoUrl, thumbnailSecond = 0 }) => {
  const { t } = useTranslation();
  const [thumbnailSrc, setThumbnailSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const lastVideoUrlRef = useRef(null);
  const lastThumbnailSecondRef = useRef(null);

  useEffect(() => {
    if (!videoUrl) {
      setLoading(false);
      setError(true);
      setThumbnailSrc(null);
      lastVideoUrlRef.current = null;
      lastThumbnailSecondRef.current = null;
      return;
    }

    // Check if videoUrl or thumbnailSecond actually changed
    const urlChanged = lastVideoUrlRef.current !== videoUrl;
    const secondChanged = lastThumbnailSecondRef.current !== thumbnailSecond;

    // If we already have a thumbnail and nothing changed, don't regenerate
    if (thumbnailSrc && !urlChanged && !secondChanged) {
      return;
    }

    // Update refs
    lastVideoUrlRef.current = videoUrl;
    lastThumbnailSecondRef.current = thumbnailSecond;

    // Only reset if URL or second changed
    if (urlChanged || secondChanged) {
      setThumbnailSrc(null);
      setLoading(true);
      setError(false);
    }

    let isMounted = true;
    let thumbnailCaptured = false;
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const generateThumbnail = () => {
      if (!isMounted || thumbnailCaptured) {
        return;
      }
      try {
        const seekTime = Math.max(0, thumbnailSecond || 0);
        video.currentTime = seekTime;
      } catch (err) {
        logger.error("Error seeking video:", { err: sanitizeError(err) });
        if (isMounted && !thumbnailCaptured) {
          setError(true);
          setLoading(false);
        }
      }
    };

    const captureFrame = () => {
      if (!isMounted || thumbnailCaptured) {
        return;
      }
      try {
        const width = video.videoWidth || 640;
        const height = video.videoHeight || 360;

        if (width === 0 || height === 0) {
          // Wait a bit more if dimensions aren't ready
          setTimeout(() => {
            if (isMounted && !thumbnailCaptured && video.videoWidth > 0) {
              captureFrame();
            }
          }, 100);
          return;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(video, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);

        if (isMounted && !thumbnailCaptured) {
          thumbnailCaptured = true;
          setThumbnailSrc(dataUrl);
          setLoading(false);
          setError(false);
        }
      } catch (err) {
        logger.error("Error capturing frame:", { err: sanitizeError(err) });
        if (isMounted && !thumbnailCaptured) {
          setError(true);
          setLoading(false);
        }
      }
    };

    const handleLoadedMetadata = () => {
      if (!isMounted || thumbnailCaptured) {
        return;
      }
      generateThumbnail();
    };

    const handleSeeked = () => {
      if (!isMounted || thumbnailCaptured) {
        return;
      }
      captureFrame();
    };

    const handleError = (e) => {
      logger.error("Video loading error:", { err: sanitizeError(e) });
      if (isMounted && !thumbnailCaptured) {
        setError(true);
        setLoading(false);
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("seeked", handleSeeked);
    video.addEventListener("error", handleError);

    video.src = generateAssetUrl(videoUrl);

    // Timeout fallback
    const timeout = setTimeout(() => {
      if (isMounted && !thumbnailCaptured) {
        logger.warn("Thumbnail generation timeout");
        setError(true);
        setLoading(false);
      }
    }, 10000); // 10 second timeout

    return () => {
      isMounted = false;
      thumbnailCaptured = true; // Prevent any further captures
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("error", handleError);
      video.src = "";
      video.load(); // Reset video element
      clearTimeout(timeout);
    };
  }, [videoUrl, thumbnailSecond]);

  if (error || !videoUrl) {
    return (
      <div className={styles.thumbnailPlaceholder}>
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M8 5V19L19 12L8 5Z" fill="currentColor" />
        </svg>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.thumbnailPlaceholder}>
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M8 5V19L19 12L8 5Z" fill="currentColor" />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={thumbnailSrc}
      alt={t("common.videoThumbnailAlt")}
      className={styles.thumbnailImage}
    />
  );
};

export default VideoThumbnail;
